// auth.ts - Supabase Auth Service
import { supabase } from '../lib/supabase';
import type { User } from '../types';

// User cache to prevent repeated database queries
// Make cache accessible for clearing
export const userCache: {
  user: User | null;
  timestamp: number;
} = {
  user: null,
  timestamp: 0
};

const CACHE_DURATION_MS = 60000; // 1 minute cache

export const authService = {
  // Clear the user cache - useful for registration flows
  clearCache: () => {
    userCache.user = null;
    userCache.timestamp = 0;
  },

  // Sign in with email and password
  signIn: async (email: string, password: string): Promise<User> => {
    // Clear cache on new sign in attempt
    authService.clearCache();
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });      if (error) {
        // Provide user-friendly error messages based on Supabase error responses
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password');
        } else if (error.message.includes('Email not confirmed')) {
          throw new Error('Email not confirmed');
        } else if (error.message.includes('Signup not allowed') || 
                   error.message.includes('User not found')) {
          throw new Error('User not found');
        } else if (error.message.includes('Too many requests')) {
          throw new Error('Too many login attempts. Please try again later.');
        } else if (error.message.includes('Invalid email')) {
          throw new Error('Invalid email');
        }
        throw error;
      }

      if (!data.user) {
        throw new Error('No user returned from sign in');
      }

      // Get full user details
      const user = await authService.getCurrentUser(true); // Force refresh
      if (!user) {
        throw new Error('Failed to get user details after sign in');
      }

      return user;
    } catch (error) {
      console.error('Sign in error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred during sign in');
    }
  },
  // Sign up with email and password
  signUp: async (
    email: string, 
    password: string, 
    firstName: string, 
    lastName: string
  ): Promise<User & { emailConfirmationPending?: boolean }> => {
    // Clear cache before registration attempt
    authService.clearCache();    // Check if user already exists before attempting signup
    try {
      // Check if user already exists in our users table
      const { data: existingUser } = await supabase
        .from('users')
        .select('email')
        .eq('email', email)
        .single();
      
      if (existingUser) {
        throw new Error('User already exists');
      }
    } catch (error: unknown) {
      // If the error is user already exists, throw it
      if (error instanceof Error && error.message === 'User already exists') {
        throw error;
      }
      // For other errors (like "no rows"), continue with signup
    }

    // Additional check: Try a dummy sign-in to see if auth user exists
    try {
      const { error: dummySignInError } = await supabase.auth.signInWithPassword({
        email,
        password: 'dummy-invalid-password-for-check'
      });
      
      // If we get "Invalid login credentials", the email exists but password is wrong
      if (dummySignInError && dummySignInError.message.includes('Invalid login credentials')) {
        throw new Error('User already exists');
      }
    } catch (signInError: unknown) {
      if (signInError instanceof Error && signInError.message === 'User already exists') {
        throw signInError;
      }
      // Continue with signup for other errors
    }

    try {      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          }
        }
      });if (error) {
        // Handle specific Supabase error messages
        if (error.message.includes('User already registered') || 
            error.message.includes('already exists') ||
            error.message.includes('duplicate key') ||
            error.message.includes('unique constraint') ||
            error.message.includes('already been registered')) {
          throw new Error('User already exists');
        }
        
        // Check for rate limiting
        if (error.message.includes('rate limit') || error.status === 429) {
          throw new Error('Too many attempts. Please try again later.');
        }
        
        // Handle email confirmation required
        if (error.message.includes('Signup not allowed for this instance')) {
          throw new Error('Registration is currently disabled. Please contact support.');
        }
        
        throw error;
      }      if (!data.user) {
        throw new Error('Failed to create user account');
      }      // Additional check: If we get a user back but they were already authenticated,
      // this might indicate the user already existed
      if (data.session && data.user.email_confirmed_at) {
        // User was immediately signed in - this shouldn't happen for new users unless
        // email confirmation is disabled. Let's double-check if this user existed.
        try {
          const { data: existingUserCheck } = await supabase
            .from('users')
            .select('id')
            .eq('email', email)
            .single();
          
          if (existingUserCheck) {
            throw new Error('User already exists');
          }
        } catch (checkError: unknown) {
          if (checkError instanceof Error && checkError.message === 'User already exists') {
            throw checkError;
          }
          // Continue if user doesn't exist in our table yet
        }
      }

      // Check if user was created recently (within last minute) vs existing user
      if (data.user.created_at) {
        const createdAt = new Date(data.user.created_at);
        const now = new Date();
        const timeDifference = now.getTime() - createdAt.getTime();
        
        // If user was created more than 2 minutes ago, they already existed
        if (timeDifference > 120000) { // 2 minutes in milliseconds
          throw new Error('User already exists');
        }
      }

      // Check if email confirmation is required
      const emailConfirmationPending = !data.session;      // Create user profile in our users table
      const { error: profileError } = await supabase
        .from('users')
        .insert({
          id: data.user.id,
          email: data.user.email!,
          first_name: firstName,
          last_name: lastName,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (profileError) {
        console.error('Error creating user profile:', profileError);
        
        // If this is a duplicate key error, the user already exists
        if (profileError.message.includes('duplicate key') || 
            profileError.message.includes('unique constraint') ||
            profileError.code === '23505') {
          throw new Error('User already exists');
        }
        
        // For other errors, log but don't throw - user is created in auth
      }// Return a user object with the emailConfirmationPending flag
      const user: User & { emailConfirmationPending?: boolean } = {
        id: data.user.id,
        email: data.user.email!,
        firstName,
        lastName,
        phone: '',
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: ''
        },
        orders: [],
        isAdmin: false,
        emailConfirmationPending
      };

      return user;
    } catch (error) {
      console.error('Sign up error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred during registration');
    }
  },

  // Sign out
  signOut: async (): Promise<void> => {
    // Clear cache immediately
    authService.clearCache();
    
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
  },

  // Get current user with caching
  getCurrentUser: async (forceRefresh = false): Promise<User | null> => {
    // Check cache first unless force refresh is requested
    if (!forceRefresh && userCache.user && Date.now() - userCache.timestamp < CACHE_DURATION_MS) {
      return userCache.user;
    }

    try {
      const { data: { user: authUser }, error } = await supabase.auth.getUser();
      
      if (error || !authUser) {
        authService.clearCache();
        return null;
      }

      // Get user profile from users table
      const { data: profile } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single();

      // Check if user is admin
      const { data: adminData } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', authUser.id)
        .single();      const user: User = {
        id: authUser.id,
        email: authUser.email!,
        firstName: profile?.first_name || authUser.user_metadata?.first_name || '',
        lastName: profile?.last_name || authUser.user_metadata?.last_name || '',
        phone: profile?.phone || '',
        address: {
          street: profile?.address?.street || '',
          city: profile?.address?.city || '',
          state: profile?.address?.state || '',
          zipCode: profile?.address?.zipCode || ''
        },
        orders: [],
        isAdmin: !!adminData
      };

      // Update cache
      userCache.user = user;
      userCache.timestamp = Date.now();

      return user;
    } catch (error) {
      console.error('Error getting current user:', error);
      authService.clearCache();
      return null;
    }
  },
  // Verify if user is admin
  isAdmin: async (): Promise<boolean> => {
    const user = await authService.getCurrentUser();
    return user?.isAdmin || false;
  },

  // Send password reset email
  resetPasswordForEmail: async (email: string): Promise<void> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Password reset error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An error occurred while sending the password reset email');
    }
  },

  // Update user password
  updatePassword: async (newPassword: string): Promise<void> => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Password update error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An error occurred while updating the password');
    }
  }
};
